import CancelablePromise from '@ms/odsp-utilities/lib/async/Promise';
import Async from '@ms/odsp-utilities/lib/async/Async';
import Retry from '@ms/odsp-utilities/lib/async/Retry';
import QosEvent, { ResultTypeEnum, IQos } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import ApiEvent, { IApiEndSchema } from '@ms/odsp-utilities/lib/logging/events/Api.event';
import { Engagement } from '@ms/odsp-utilities/lib/logging/events/Engagement.event';
import { RUMOneLogger } from '@ms/odsp-utilities/lib/logging/rumone/RUMOneLogger';
import { IQosEndSchema } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';
import { ApiError } from '@ms/utilities-error/lib/ApiError';
import { SimpleUri } from '@ms/odsp-utilities/lib/uri/SimpleUri';
import { IExtraData } from '@ms/telemetry-manager/lib/events/IExtraData';

import ServerData, { IErrorData } from './ServerData';
import ServerConnection from './ServerConnection';
import ISpPageContext from '../../interfaces/ISpPageContext';

const currentWindow = typeof window !== 'undefined' ? window : undefined;

const MovePromiseTelemetry = !Killswitch.isActivated('9C775E57-F288-4397-93CF-BC78D86C5E52', '5/21/2019', 'Repurpose {qosName}.Promise telemetry');
const RemoveDuplicateRetry = !Killswitch.isActivated('3022DEEB-46FC-4507-BE47-A36BD6817672', '5/21/2019', 'Remove duplicated retry logic');
const RemoveCompleteEvent = !Killswitch.isActivated('34E7705F-1331-4283-A60D-BD484EC3E037', '5/21/2019', 'Remove unused {qosName}Complete event');

export type IDataRequestorPageContext = Pick<ISpPageContext,
    'webServerRelativeUrl' |
    'webAbsoluteUrl' |
    'updateFormDigestPageLoaded' |
    'serverTime' |
    'formDigestValue' |
    'formDigestTimeoutSeconds' |
    'authToken' |
    'getAuthToken'>;

export interface IDataRequestorParams {
    /**
     * The base QoS name to use for all requests.
     * Calls to `getData` will append their own provided `qosName` values to this base value.
     */
    qosName?: string;
}

export interface IDataRequestorLegacyParams extends IDataRequestorParams {
    /**
     * A current page context. Prefer passing this value in dependencies.
     * `DataRequestor` minimally requires `webServerRelativeUrl` to be set.
     * Additionally, settings `updateFormDigestPageLoaded` can help avoid extra server calls.
     */
    pageContext?: IDataRequestorPageContext;
}

export interface IDataRequestorDependencies {
    /**
     * A current page context.
     * `DataRequestor` minimally requires `webServerRelativeUrl` to be set.
     * Additionally, settings `updateFormDigestPageLoaded` can help avoid extra server calls.
     */
    pageContext?: IDataRequestorPageContext;
    qosEvent?: typeof QosEvent;
    apiEvent?: typeof ApiEvent;
    engagement?: typeof Engagement;
}

export interface IDataRequestor {
    getData<T>(options: IDataRequestGetDataOptions<T>): CancelablePromise<T>;
}

export type IQosEndSchemaResult = Pick<IQosEndSchema, 'resultCode' | 'resultType'>;

export function isQosEndSchemaOverride(item: string | IQosEndSchema): item is IQosEndSchemaResult {
    return typeof item === 'object';
}

export { IErrorData };

export interface IDataRequestGetDataOptions<T> {
    url: string;
    /**
     * Optional override for parsing the server response.
     * The default behavior to parse as JSON and cast to `T`.
     * Only override this to handle the response string directly or convert to a different data type.
     */
    parseResponse?: (responseText: string, serverData: ServerData) => T;
    /**
     * The name for the call for QoS reporting.
     */
    qosName: string;
    /**
    * Data to be included in the qos end event for this request.
    */
    qosExtraData?: IQosEndSchema['extraData'];
    /**
    * Optional handler to decide proper result code for QOS event objects
    *
    * @returns {string} result code to be set on the QOS event object associated with this operation.
    * @returns {IQosEndSchema} Qos end schema to be overriden
    */
    qosHandler?: (errorData: IErrorData) => string | IQosEndSchemaResult;

    /**
    * Optional QOS logging function by consuming the data requestor qos data, currently used for app level QOS logging
    */
    customQosLogger?: (qosData: IQosEndSchema) => void;
    /**
     * The HTTP method to use for the request.
     */
    method?: string;
    /**
     * Data to put in the body of thr request.
     * Objects should be serialized to JSON first.
     */
    additionalPostData?: string | Blob;
    /**
     * Additional headers to include in the request.
     * Headers minimally necessary for SharePoint calls will be provided automatically.
     */
    additionalHeaders?: { [key: string]: string };
    /**
     * The request content type.
     * Default is 'application/json;odata=verbose'.
     */
    contentType?: string;
    /**
     * The maximum number of retries to make in response to retriable errors.
     * Default is 0.
     */
    maxRetries?: number;
    /**
     * Whether or not to disable automatic redirects to the login page if the session expired.
     */
    noRedirect?: boolean;
    /**
     * Whether or not this call is being made across a site collection.
     * Unnecessary if the full URL is provided.
     */
    crossSiteCollectionCall?: boolean;
    /**
     * The expected response type.
     */
    responseType?: string;
    /**
     * Specify whether or not up-to-date digest information must be provided for this request.
     */
    needsRequestDigest?: boolean;
    /**
     * A handler for upload progress events.
     */
    onUploadProgress?: (event: ProgressEvent) => void;
    /**
     * timeout in milliseconds before cancelling the request promise
     */
    timeout?: number;
    /**
     * An auth token to use for the request.
     */
    authToken?: string;
    /**
     * specify whether or not timeout is also applied to last retry, default is false.
     * Set it to false when not doing timeout for the last retry to guarantee data can be received regardless performance
     */
    noTimeoutInLastRetry?: boolean;
}

export interface IDataRequestGetDataAsBlobOptions extends IDataRequestGetDataOptions<Blob> {
    responseType: 'blob';
}

export class DataRequestor implements IDataRequestor {
    private _pageContext: IDataRequestorPageContext | undefined;

    private _qosName: string;

    private _async: Async;

    private _qosEvent: typeof QosEvent;
    private _apiEvent: typeof ApiEvent;
    private _engagement: typeof Engagement;

    public static parseJSON<T>(responseText: string): T {
        return responseText && JSON.parse(responseText) || undefined;
    }

    constructor(params: IDataRequestorLegacyParams);
    constructor(params: IDataRequestorParams, dependencies: IDataRequestorDependencies);
    constructor(params: IDataRequestorParams, dependencies?: IDataRequestorDependencies) {
        this._pageContext = dependencies ? dependencies.pageContext : (params as IDataRequestorLegacyParams).pageContext;
        this._qosName = params.qosName;
        this._qosEvent = dependencies && dependencies.qosEvent || QosEvent;
        this._apiEvent = dependencies && dependencies.apiEvent || ApiEvent;
        this._engagement = dependencies && dependencies.engagement || Engagement;
        this._async = new Async(this);
    }

    public getData(options: IDataRequestGetDataAsBlobOptions): CancelablePromise<Blob>;
    public getData<T>(options: IDataRequestGetDataOptions<T>): CancelablePromise<T>;
    public getData<T>({
        url,
        parseResponse = <(responseText: string, serverData: ServerData) => T>DataRequestor.parseJSON,
        qosName,
        qosExtraData,
        qosHandler,
        customQosLogger,
        additionalPostData = '',
        additionalHeaders,
        contentType,
        method = 'POST',
        maxRetries = 0,
        crossSiteCollectionCall = false,
        noRedirect = false,
        needsRequestDigest = true,
        responseType,
        onUploadProgress,
        timeout,
        authToken: specifiedAuthToken,
        noTimeoutInLastRetry = false
    }: IDataRequestGetDataOptions<T>): CancelablePromise<T | Blob> {
        maxRetries = Math.max(maxRetries, 0);
        let numRetries: number = 0;

        const pageContext = this._pageContext;

        const getDataPromise = (authToken?: string): CancelablePromise<T | Blob> => {

            // Add Authorization header to request if authToken exists on _spPageContext
            // and an Authorization header doesn't already exist on the request.
            if (authToken) {
                const authorizationHeaderValue = `Bearer ${authToken}`;

                // Add Authorization header if it doesn't exist already.
                if (additionalHeaders) {
                    let authorizationHeaderSpecified = false;
                    for (const headerKey in additionalHeaders) {
                        if (headerKey) {
                            if (headerKey.toLowerCase() === 'authorization') {
                                authorizationHeaderSpecified = true;
                            }
                        }
                    }

                    if (!authorizationHeaderSpecified) {
                        additionalHeaders['Authorization'] = authorizationHeaderValue;
                    }
                } else {
                    additionalHeaders = {
                        'Authorization': authorizationHeaderValue
                    };
                }

                // Since Authorization header is present, the request is cross-origin.
                crossSiteCollectionCall = true;
                needsRequestDigest = false;
                noRedirect = true;
            }

            let serverConnection = new ServerConnection({
                ...(pageContext ? {
                    webServerRelativeUrl: pageContext.webServerRelativeUrl,
                    webAbsoluteUrl: crossSiteCollectionCall ? pageContext.webAbsoluteUrl : undefined,
                    formDigest: {
                        // Pull current digest state from the page.
                        // This helps initial requests avoid extra server calls.
                        // Unit tests will need to spoof this in order to avoid the need to mock the digest response.
                        serverTime: pageContext.serverTime,
                        updateFormDigestPageLoaded: pageContext.updateFormDigestPageLoaded,
                        formDigestValue: pageContext.formDigestValue,
                        formDigestTimeoutSeconds: pageContext.formDigestTimeoutSeconds
                    }
                } : {}),
                needsRequestDigest: needsRequestDigest,
                engagement: this._engagement,
                operationName: this._qosName
            });

            let qosNames: string[] = [];

            if (this._qosName) {
                qosNames.push(this._qosName);
            }

            if (qosName) {
                qosNames.push(qosName);
            }

            qosName = qosNames.join('.');
            const logCustomQos = (qosData: IQosEndSchema) => {
                if (customQosLogger) {
                    customQosLogger(qosData);
                }
            };

            const currentOrigin = currentWindow && currentWindow.location && new SimpleUri(currentWindow.location.href).authority;
            const targetOrigin = new SimpleUri(url).authority;

            let isCanceled: boolean;
            let apiEvent: IQos;
            let serviceWorkerDataSource: string;

            /* tslint:disable: no-any */
            let onExecute = (complete: (argData?: T | Blob) => void, error: (err?: IErrorData) => void) => {
                /* tslint:enable: no-any */
                // forward declaration to shut up linter
                let doGetData: () => void;

                const onDataSuccess = (apiEvent?: IQos) => (serverData: ServerData) => {
                    let data: T | Blob;
                    let qos = new this._qosEvent({ name: qosName });
                    const response = serverData.getResponseText();
                    serviceWorkerDataSource = serverData.getServiceWorkerDataSourceHeader();
                    const extraData = {
                        ...(qosExtraData || {}),
                        needsRequestDigest,
                        hasAuthToken: !!authToken,
                        isCrossOrigin: !!currentOrigin && currentOrigin !== targetOrigin,
                        CorrelationId: serverData.getCorrelationId(),
                        HttpStatus: serverData.getStatus(),
                        serviceWorkerDataSource: serviceWorkerDataSource || undefined
                    };
                    let parsedResponse = false;
                    try {
                        if (isBlobResponse(responseType, response)) {
                            data = response;
                        } else {
                            data = parseResponse(response, serverData);
                        }
                        // This line could throw if the response does not have the expected data type.
                        // For example, there is at least one handler (SpoSuiteLinks.ashx) that will return
                        // an HTML error page rather than the expected JSON on error. We should count that case
                        // as a failure even though *something* was successfully returned.
                        parsedResponse = true;
                        const qosData = {
                            resultType: ResultTypeEnum.Success,
                            extraData: extraData
                        };
                        qos.end(qosData);
                        if (apiEvent) {
                            apiEvent.end(qosData);
                        }
                        logCustomQos(qosData);
                    } catch (ex) {
                        // The response did not parse properly.
                        const qosData = {
                            resultType: ResultTypeEnum.Failure,
                            resultCode: 'InvalidJSON',
                            error: (typeof ex === 'object' ? JSON.stringify(ex) : ex),
                            extraData: extraData
                        };
                        qos.end(qosData);
                        if (apiEvent) {
                            apiEvent.end(qosData);
                        }
                        logCustomQos(qosData);
                        // let the caller know there was an error in parsing the data
                        error();
                    }

                    // pass the response to the caller
                    if (parsedResponse) {
                        if (RemoveCompleteEvent) {
                            try {
                                complete(data);
                            } catch (ex) {
                                // the caller failed to handle the data
                                // no-op
                            }
                        } else {
                            qos = new this._qosEvent({ name: qosName + 'Complete' });
                            try {
                                complete(data);
                                const qosData = {
                                    resultType: ResultTypeEnum.Success,
                                    extraData: extraData
                                };
                                qos.end(qosData);
                                logCustomQos(qosData);
                            } catch (ex) {
                                // the caller failed to handle the data
                                const qosData = {
                                    resultType: ResultTypeEnum.Failure,
                                    error: (typeof ex === 'object' ? JSON.stringify(ex) : ex),
                                    extraData: extraData
                                };
                                qos.end(qosData);
                                logCustomQos(qosData);
                            }
                        }
                    }
                    serverConnection = undefined;
                };

                const onError = (apiEvent?: IQos) => (serverData?: ServerData, requestError?: Error) => {
                    const qos = new this._qosEvent({
                        name: qosName
                    });

                    let extraData: IExtraData = {
                        ...(qosExtraData || {}),
                        needsRequestDigest,
                        hasAuthToken: !!authToken,
                        isCrossOrigin: !!currentOrigin && currentOrigin !== targetOrigin
                    };

                    if (!serverData) {
                        let qosData: IQosEndSchema;

                        if (isCanceled) {
                            // serverData will be undefined if the request was aborted
                            qosData = {
                                resultType: ResultTypeEnum.ExpectedFailure,
                                resultCode: 'RequestAborted',
                                extraData: extraData
                            };
                        } else {
                            let code = 'Unknown';
                            let isExpected = true;
                            let status = 0;

                            if (requestError) {
                                if (requestError instanceof ApiError) {
                                    code = requestError.code;
                                    isExpected = requestError.isExpected;
                                    status = -1;
                                }
                            }

                            // Request completed, but did not return data or a useful status code. This can happen for any number of reasons:
                            // CORS Rejection (we could make a guess about this by comparing url origins)
                            // Request blocked by client (e.g. extension)
                            // Request blocked by firewall
                            qosData = {
                                resultType: isExpected ?
                                    ResultTypeEnum.ExpectedFailure :
                                    ResultTypeEnum.Failure,
                                resultCode: code,
                                extraData: extraData
                            };
                            // Ensure that Promises that wrap the data request always complete.
                            error({ status, code: '' });
                        }

                        qos.end(qosData);
                        if (apiEvent) {
                            apiEvent.end(qosData);
                        }
                        logCustomQos(qosData);

                        return;
                    }

                    const correlationId = serverData.getCorrelationId();
                    const groupThrottle = serverData.getGroupThrottle();
                    const MSCorrelationVector = serverData.getMSCorrelationVector();
                    const status = serverData.getStatus();
                    extraData = {
                        ...extraData,
                        CorrelationId: correlationId,
                        HttpStatus: status,
                        GroupThrottle: groupThrottle,
                        MSCorrelationVector: MSCorrelationVector
                    };

                    let resultType = ResultTypeEnum.Failure;
                    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
                    if (status === 403 ||
                        status === 404 ||
                        status === 429 ||
                        status === 503 ||
                        status === -1 ||
                        (status === 0 && isOffline)) {
                        // no need to retry authentication errors...
                        // no need to retry document not found errors...
                        // no need to retry resource throttling errors... (we need to back away from it. 503 is SQL throttling)
                        // no need to retry if the request was missing parameters
                        // no need to retry if we are offline
                        maxRetries = 0;
                        resultType = ResultTypeEnum.ExpectedFailure;
                    }

                    const parseQosError = (response: string | IErrorData | Blob): void => {
                        let errorData: IErrorData = undefined;
                        if (typeof response === 'object' && typeof (<IErrorData>response).status === 'number') {
                            // Test to see if the response is already an IErrorData
                            errorData = <IErrorData>response;
                        } else if (typeof response === 'string') {
                            let parsedData = undefined;
                            try {
                                parsedData = JSON.parse(response);
                            }
                            catch (ex) {
                                // np-op
                            }

                            if (parsedData) {
                                if (typeof parsedData.error === 'string') {
                                    if (parsedData.error_description) {
                                        errorData = <IErrorData>{
                                            code: `-1,${parsedData.error}`,
                                            message: parsedData.error_description
                                        };
                                    } else {
                                        errorData = <IErrorData>{
                                            message: parsedData.error
                                        };
                                    }
                                } else {
                                    errorData = <IErrorData>(parsedData.error || parsedData['odata.error'] || { responseData: parsedData });
                                }
                            }
                        }

                        errorData = <IErrorData>(errorData || {});
                        errorData.status = status;
                        const errorWithRedirect: {
                            '@error.redirectUrl'?: string;
                        } & IErrorData = errorData;

                        const payloadRedirectUrl = errorWithRedirect['@error.redirectUrl'];

                        const redirectUrl = serverData.getRedirectUrl() || payloadRedirectUrl;

                        if (redirectUrl) {
                            // If there is a process to handle the error, then it's expected.
                            resultType = ResultTypeEnum.ExpectedFailure;
                        }

                        errorData.redirectUrl = redirectUrl;

                        if (correlationId) {
                            errorData.correlationId = correlationId;
                        }
                        if (groupThrottle) {
                            errorData.groupThrottle = groupThrottle;
                        }

                        let resultCode = status.toString();
                        let apiEventCode: string;
                        let qosEndOverride: IQosEndSchema;
                        if (qosHandler) {
                            const qosHandlerResult = qosHandler(errorData);
                            if (!isQosEndSchemaOverride(qosHandlerResult)) {
                                resultCode = qosHandlerResult;
                                apiEventCode = qosHandlerResult;
                            } else {
                                qosEndOverride = qosHandlerResult;
                            }

                            if (status === 500 && (resultCode === 'DefaultDoclibNotFound' || resultCode === 'PrefetchWebMismatch')) {
                                maxRetries = 0;
                                resultType = ResultTypeEnum.ExpectedFailure;
                            }
                        }

                        const errorMessage: string = JSON.stringify(errorData);

                        const qosData: IQosEndSchema = {
                            resultType: resultType,
                            error: errorMessage,
                            resultCode: resultCode,
                            extraData: extraData,
                            ...(qosEndOverride || {})
                        };
                        qos.end(qosData);
                        if (apiEvent) {
                            if (!apiEventCode) {
                                apiEventCode = `[${status.toString()}] ${errorData.code || 'Unknown'}`;
                            }
                            apiEvent.end({
                                resultType: resultType,
                                error: errorMessage,
                                resultCode: apiEventCode,
                                extraData: extraData,
                                ...(qosEndOverride || {})
                            });
                        }
                        logCustomQos(qosData);

                        if (numRetries < maxRetries) {
                            ++numRetries;
                            doGetData();
                        } else {
                            error(errorData);
                        }
                    };

                    let responseType = serverData.getResponseType();
                    if (!responseType || responseType === 'text') {
                        let responseText = serverData.getResponseText();
                        parseQosError(responseText);
                    } else {
                        serverData.parseError().then(parseQosError);
                    }
                };

                doGetData = () => {
                    if (MovePromiseTelemetry) {
                        apiEvent = new this._apiEvent({
                            url: url,
                            name: qosName + '.Promise'
                        });
                    }
                    serverConnection.getServerDataFromUrl({
                        url: url,
                        successCallback: onDataSuccess(apiEvent),
                        failureCallback: onError(apiEvent),
                        uploadProgressCallback: onUploadProgress,
                        additionalPostData: additionalPostData,
                        method: method,
                        additionalHeaders: additionalHeaders,
                        contentType: contentType,
                        withCredentials: authToken ? false : undefined,
                        noRedirect: noRedirect,
                        responseType: responseType,
                        needsRequestDigest: needsRequestDigest
                    });
                };
                doGetData();
            };

            let onCancel = () => {
                isCanceled = true;
                if (serverConnection) {
                    serverConnection.abort();
                    if (numRetries >= maxRetries) { // do not dispose serverConnection when data requestor is in the middle of retries.
                        serverConnection.dispose();
                    }
                }
            };

            let rumOne: RUMOneLogger = RUMOneLogger.getRUMOneLogger();
            let apiStartTime: number = Date.now();

            if (rumOne) {
                if (!Boolean(rumOne.readTempData('appDataFetchStart'))) {
                    // Record the App DataFetch start before the first API call
                    rumOne.saveTempData('appDataFetchStart', apiStartTime);
                }
            }

            const createPromise: () => CancelablePromise<T | Blob> = () => {
                let promise: CancelablePromise<T | Blob>;
                const createTimeoutPromise: () => CancelablePromise<T | Blob> = () => {
                    const promise = new CancelablePromise<T | Blob>(onExecute, onCancel);
                    // when it is the last retry and noTimeoutInLastRetry is set to true, do not set timeout to allow calls to finish,
                    // we at least can get data no matter how slow
                    if (timeout && (!noTimeoutInLastRetry || numRetries < maxRetries)) {
                        return CancelablePromise.timeout(timeout, promise);
                    }
                    return promise;
                };
                if (!RemoveDuplicateRetry && numRetries < maxRetries) { //retry promise
                    promise = Retry<T | Blob>({
                        retries: maxRetries,
                        callback: createTimeoutPromise,
                        canRetry: (error: any) => true,
                        async: this._async,
                        delay: 0,
                        adjustRetries: (error: any) => maxRetries - numRetries,
                        beforeRetry: () => {
                            numRetries++;
                        }
                    });
                } else {
                    promise = createTimeoutPromise();
                }
                return promise.then((result: T | Blob) => {
                    if (serverConnection) {
                        serverConnection.dispose();
                    }
                    return result;
                }, (error: any) => {
                    if (serverConnection) {
                        serverConnection.dispose();
                    }
                    return CancelablePromise.reject(error);
                });
            };

            if (MovePromiseTelemetry) {
                return createPromise().catch((error?: IErrorData | Error) => {
                    if (!apiEvent.endTime && CancelablePromise.isCanceled(error)) {
                        // If we haven't fired the end event of the latest api event, then fire it now
                        apiEvent.end({
                            resultCode: 'PromiseCanceled',
                            resultType: ResultTypeEnum.ExpectedFailure
                        });
                    }
                    throw error;
                });
            } else {
                const qosSuccessSchema = (result: T): IApiEndSchema => {
                    return <IApiEndSchema>{
                        resultType: ResultTypeEnum.Success,
                        extraData: {
                            serviceWorkerDataSource: serviceWorkerDataSource || undefined
                        }
                    };
                }
                return this._apiEvent.instrumentPromise({
                    url: url,
                    name: qosName + '.Promise'
                }, createPromise, qosSuccessSchema, (error?: IErrorData | Error | string): IQosEndSchema => {
                    return {
                        extraData: qosExtraData,
                        ...getQosEndSchema(error)
                    };
                });
            }
        };

        return CancelablePromise.resolve(
            specifiedAuthToken ||
            (pageContext && (
                pageContext.getAuthToken && pageContext.getAuthToken() ||
                pageContext.authToken))
        ).then(getDataPromise);
    }
}

function getQosEndSchema(
    error: IErrorData | Error | string
): IQosEndSchema {
    if (error instanceof Error) {
        return {
            resultCode: error ? `${error.name}${error.message ? ` - ${error.message}` : ''}` : '',
            resultType: ResultTypeEnum.Failure,
            error: JSON.stringify(error),
            ...(error instanceof Error && CancelablePromise.isCanceled(error) ? {
                resultCode: 'Canceled',
                resultType: ResultTypeEnum.ExpectedFailure
            } : {}),
            ...(error instanceof ApiError ? {
                resultCode: error.code,
                resultType: error.isExpected ? ResultTypeEnum.ExpectedFailure : ResultTypeEnum.Failure
            } : {})
        };
    }

    if (isErrorData(error)) {
        return {
            resultCode: typeof error.code === 'string' ? error.code : `${error.status}`,
            resultType: ResultTypeEnum.Failure,
            error: JSON.stringify(error),
            ...(error.status === 403 ? {
                resultCode: 'AccessDenied',
                resultType: ResultTypeEnum.ExpectedFailure
            } : {})
        };
    }

    return {
        error: error,
        resultType: ResultTypeEnum.Failure,
        resultCode: error
    };
}

function isErrorData(error: IErrorData | string): error is IErrorData {
    return !!error && typeof error === 'object' && ('code' in (error as IErrorData));
}

function isBlobResponse(responseType: string, response: string | Blob): response is Blob {
    return responseType === 'blob' && response && typeof response === 'object';
}

export default DataRequestor;
